import { createPortal } from 'react-dom';
import { useId, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { useOutputs, usePrenotas, useStreamSummary, useVerifications, useXReviews } from 'src/api/queries';
import { ContinueProcessing } from 'src/components/editorial/ContinueProcessing';
import type { StreamRailContext } from 'src/components/editorial/StreamLayout';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import {
  buildDecisions,
  buildStageTime,
  buildStoryRows,
  type Decision,
  type DecisionKind,
  type StoryRow,
} from 'src/lib/streamBoard';
import { GATE_LABEL } from 'src/lib/reader';

/**
 * Answers: what did this broadcast produce, and where does it need me?
 *
 * The stage rail on the shell carries the process standing, so this screen spends its column on
 * the journalism: the stories and the rerun control. A mañanera produces a dozen stories and two
 * of them need a decision — those two live beside the rail, read once, and then the rail stays.
 */
export function OverviewWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  // The decisions answer mounts in the shell's rail column, above the stage rail — read once, then
  // it scrolls away while the rail stays pinned. The mount point lives in the layout so the same
  // column carries the queue note on every stage screen; only the Resumen has decisions to give.
  const { railTop } = useOutletContext<StreamRailContext>();
  const summary = useStreamSummary(streamId);
  // The roster joins four payloads on story_id. Every one of them is already loaded by a screen
  // the editor reaches from here, so React Query serves them from cache in both directions.
  const prenotas = usePrenotas(streamId);
  const xReviews = useXReviews(streamId);
  const verifications = useVerifications(streamId);
  const outputs = useOutputs(streamId);

  if (summary.isPending) {
    return <Skeleton variant="rows" count={6} />;
  }

  if (summary.isError) {
    return (
      <LoadFailure title="No se pudo cargar el estado de la transmisión" error={summary.error} onRetry={summary.refetch} />
    );
  }

  const counts = summary.data.summary;

  const boardReady = Boolean(prenotas.data && xReviews.data && verifications.data && outputs.data);
  const boardLoading = prenotas.isPending || xReviews.isPending || verifications.isPending || outputs.isPending;
  const rows = boardReady
    ? buildStoryRows({
        prenotas: prenotas.data?.prenotas ?? [],
        xReviews: xReviews.data?.xReviews ?? [],
        verifications: verifications.data?.verifications ?? [],
        outputs: outputs.data?.outputs ?? [],
      })
    : [];
  const decisions = buildDecisions(rows);
  const stageTime = buildStageTime(outputs.data?.outputs ?? []);

  return (
    <>
      {boardLoading && <Skeleton variant="rows" count={4} />}

      {boardReady && <StoryRoster rows={rows} streamId={streamId} />}

      {/* Below the editorial work rather than above it: the rail already shows where the run
          stopped, and what an editor does about a story outranks what they do about a stage. */}
      <ContinueProcessing streamId={streamId} summary={counts} />

      <section className="quiet-tail">
        <h2>Procedencia de esta pantalla</h2>
        <p>
          Cada cifra del listón se cuenta en la base de datos, en una sola consulta: intervenciones del guion,
          señales, coincidencias de criterio, candidatos, prenotas, revisiones de X, verificaciones, borradores y
          revisiones editoriales. Ninguna es un estimado. El listado de historias se arma en el navegador cruzando
          prenotas, revisiones de X, verificaciones y borradores por <code>story_id</code>.
        </p>
        {stageTime.length > 0 && (
          <p className="overview__stage-time">
            Tiempo de generación por etapa, sumado sobre {outputs.data?.outputs.length ?? 0} borradores:{' '}
            {stageTime.map(({ stage, ms }) => `${stage} ${Math.round(ms / 1000)}s`).join(' · ')}.
          </p>
        )}
      </section>

      {/* Rendered through a portal into the shell's rail column, beside the stage rail — the
          Resumen's answer sits where the mockup's notice sits, and only the rail below it pins. */}
      {railTop &&
        boardReady &&
        createPortal(
          <DecisionsRegion decisions={decisions} streamId={streamId} storiesWithoutWork={rows.length > 0} />,
          railTop
        )}
    </>
  );
}

const DECISION_LABEL: Record<DecisionKind, string> = {
  contradiction: 'Contradicción material',
  draft_blocked: 'Versión bloqueada',
  human_review: 'Revisión humana obligatoria',
  verification_blocked: 'Verificación sin salida',
  research_failed: 'Investigación incompleta',
  no_draft: 'Sin versión redactada',
};

const DECISION_TONE: Record<DecisionKind, string> = {
  contradiction: 'lead',
  draft_blocked: 'contradicted',
  human_review: 'contradicted',
  verification_blocked: 'partial',
  research_failed: 'partial',
  no_draft: 'unresolved',
};

/**
 * The answer to the screen's question, at primary weight.
 *
 * Nothing to decide is a finding too, and it says which checks it is the absence of — an empty
 * region that says "todo en orden" and nothing else teaches an editor that the tool is not
 * looking (Art Direction §13).
 */
function DecisionsRegion({
  decisions,
  streamId,
  storiesWithoutWork,
}: {
  decisions: Decision[];
  streamId: string;
  storiesWithoutWork: boolean;
}) {
  if (decisions.length === 0) {
    return (
      <section className="decisions decisions--clear" role="status">
        <span className="decisions__mark" aria-hidden="true">
          <Icon name="check" />
        </span>
        <div>
          <strong className="decisions__verdict">Nada pendiente de decisión</strong>
          <p className="decisions__why">
            {storiesWithoutWork
              ? 'Ninguna historia quedó en disputa, ninguna versión fue bloqueada y ningún hecho de riesgo espera a una persona.'
              : 'Esta transmisión todavía no produjo historias, así que no hay nada que decidir sobre ellas.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="decisions">
      <h2 className="decisions__title">
        Requiere decisión <span className="decisions__count">{decisions.length}</span>
      </h2>
      <ul className="decisions__list">
        {decisions.map((decision, index) => (
          <li key={`${decision.kind}-${decision.storyId}-${index}`}>
            <Link className="decision" to={`/stream/${encodeURIComponent(streamId)}/${decision.to}`} data-nav>
              <span className="decision__kind" data-tone={DECISION_TONE[decision.kind]}>
                {DECISION_LABEL[decision.kind]}
              </span>
              <span className="decision__headline">{decision.headline}</span>
              <span className="decision__detail">{decision.detail}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const X_STATUS_NOTE: Record<StoryRow['xStatus'], string> = {
  scored: '',
  skipped: 'X omitida',
  failed: 'X falló',
  absent: 'sin X',
};

const VERIFICATION_LABEL: Record<string, string> = {
  verified: 'Verificada',
  contradiction_lead: 'En disputa',
  blocked_critical_claim: 'Claim crítico abierto',
  blocked_no_sources: 'Sin fuente',
  blocked_input: 'Entrada inválida',
  research_failed: 'Investigación incompleta',
};

const VERIFICATION_TONE: Record<string, string> = {
  verified: 'supported',
  contradiction_lead: 'lead',
  blocked_critical_claim: 'contradicted',
  blocked_no_sources: 'contradicted',
  blocked_input: 'contradicted',
  research_failed: 'partial',
};

/**
 * Every story of the broadcast on one line: what it is, how loud it was, whether it holds up, and
 * how many versions of it exist. Ordered by what needs attention, then by resonance — never by id,
 * which is the order a database returns and no editor asked for.
 *
 * A real table inside a card, and the whole row is the doorway: pointer users click anywhere on
 * the line, keyboard users get the same destination through the row's link semantics. The
 * headline stays at reading weight — a table of titles in bold is a table that shouts every line.
 */
function StoryRoster({ rows, streamId }: { rows: StoryRow[]; streamId: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const searchId = useId();
  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      needle
        ? rows.filter(
            row =>
              row.headline.toLowerCase().includes(needle) || row.storyId.toLowerCase().includes(needle)
          )
        : rows,
    [rows, needle]
  );

  const openStory = () => navigate(`/stream/${encodeURIComponent(streamId)}/notes`);

  return (
    <section className="block">
      <div className="block-head">
        <h2 className="section-title">Historias de la transmisión</h2>
        <span className="block-head__caption">
          {needle ? `${visible.length} de ${rows.length} historias` : `${rows.length} generadas`}
        </span>
      </div>

      <div className="filters-card">
        <div className="filters-card__field filters-card__field--grow">
          <label htmlFor={searchId}>Buscar</label>
          <div className="search-box">
            <Icon name="search" />
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar por título o código de historia"
              autoComplete="off"
            />
          </div>
        </div>
        <button type="button" className="filters-card__clear" onClick={() => setQuery('')}>
          Limpiar
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="board-table__scroll">
          <AbsenceState kind="empty" what={`Ninguna historia coincide con «${query}».`} />
        </div>
      ) : (
        <div className="board-table__scroll">
          <table className="board-table board-table--stories">
            <caption className="visually-hidden">Historias generadas en la transmisión</caption>
            <thead>
              <tr>
                <th scope="col">Historia</th>
                <th scope="col" className="board-table__num">
                  Relevancia
                </th>
                <th scope="col">Verificación</th>
                <th scope="col" className="board-table__num">
                  Borrador
                </th>
                <th scope="col">
                  <span className="visually-hidden">Abrir</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr
                  key={row.storyId}
                  className="board-table__row"
                  role="link"
                  tabIndex={0}
                  aria-label={`Abrir ${row.headline}`}
                  onClick={openStory}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openStory();
                    }
                  }}
                >
                  <td>
                    <p className="board__headline">{row.headline}</p>
                    <p className="board__ids">
                      <span className="mono">{row.storyId.split('_').pop()}</span>
                      {row.eligibility !== 'allowed' && (
                        <span className="board__chip" data-tone="unresolved">
                          no elegible
                        </span>
                      )}
                      {row.drafts.total === 0 && row.absenceReason && (
                        <span className="board__reason">{row.absenceReason}</span>
                      )}
                      {row.gateCodes.map(code => (
                        <span
                          key={code}
                          className="board__chip"
                          data-tone={code.startsWith('STYLE') ? 'partial' : 'contradicted'}
                          title={GATE_LABEL[code] ?? code}
                        >
                          {code}
                        </span>
                      ))}
                    </p>
                  </td>
                  <td className="board-table__num">
                    {row.xScore === null ? (
                      <span className="board__null">{X_STATUS_NOTE[row.xStatus]}</span>
                    ) : (
                      <>
                        <span className="board__figure">{Math.round(row.xScore)}</span>{' '}
                        <span className="board__unit">en X</span>
                      </>
                    )}
                  </td>
                  <td>
                    {row.verification ? (
                      <span className="board__chip" data-tone={VERIFICATION_TONE[row.verification] ?? 'unresolved'}>
                        {VERIFICATION_LABEL[row.verification] ?? row.verification}
                      </span>
                    ) : (
                      <span className="board__null">sin verificar</span>
                    )}
                  </td>
                  <td className="board-table__num">
                    {row.drafts.total === 0 ? (
                      // Skipped-with-reason, not a blank: the four absence states are four different
                      // facts about the world (Art Direction §7).
                      <span className="board__null" title={row.absenceReason}>
                        {row.absenceReason ? 'omitida' : 'sin versión'}
                      </span>
                    ) : (
                      <>
                        <span className="board__drafts">
                          <span data-tone="supported">{row.drafts.ready}</span>
                          <span className="board__slash">/</span>
                          <span>{row.drafts.total}</span>
                        </span>
                        <span className="board__unit">
                          {row.drafts.words > 0 ? `· ${row.drafts.words} palabras` : '· listas'}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className="board__open" aria-hidden="true">
                      →
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
